if (!customElements.get('hoobro-collection-navigation')) {
  customElements.define('hoobro-collection-navigation', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.items = this.querySelector('.hb-home__items');
      this.panels = Array.from(this.querySelectorAll('[data-panel]'));
      this.tabs = Array.from(this.querySelectorAll('[data-tab]'));
      this.controls = this.querySelector('.hb-home__controls');
      this.previous = this.querySelector('[data-direction="previous"]');
      this.next = this.querySelector('[data-direction="next"]');
      if (!this.panels.length) return;
      if (this.dataset.layout === 'rooms') {
        this.hoverSwitch = this.dataset.hoverSwitch === 'true';
        this.autoplayEnabled = this.dataset.autoplay === 'true';
        this.autoplayDelay = (Number(this.dataset.autoplaySeconds) || 5) * 1000;
        this.pauseReasons = new Set();
        if (document.hidden) this.pauseReasons.add('document');
        if (this.matches(':hover')) this.pauseReasons.add('pointer');
        if (this.contains(document.activeElement) && document.activeElement.matches(':focus-visible')) this.pauseReasons.add('focus');
        this.pauseReasons.add('viewport');
        this.setAttribute('data-enhanced', '');
        this.querySelector('.hb-home__tabs').hidden = false;
        this.panels.forEach((panel, index) => {
          panel.setAttribute('role', 'region');
          panel.setAttribute('aria-labelledby', this.tabs[index].id);
        });
        this.activate(0);
        this.tabs.forEach((tab, index) => {
          tab.addEventListener('click', () => {
            if (!tab.hasAttribute('href')) this.activate(index);
          }, { signal });
          tab.addEventListener('pointerenter', event => {
            if (this.hoverSwitch && event.pointerType !== 'touch') this.activate(index);
          }, { signal });
          tab.addEventListener('keydown', event => {
            let target;
            if (event.key === 'ArrowRight') target = (index + 1) % this.tabs.length;
            if (event.key === 'ArrowLeft') target = (index - 1 + this.tabs.length) % this.tabs.length;
            if (event.key === 'Home') target = 0;
            if (event.key === 'End') target = this.tabs.length - 1;
            if (target === undefined) return;
            event.preventDefault();
            this.setPaused('focus', true);
            this.activate(target);
            this.tabs[target].focus();
          }, { signal });
        });
        this.controls.hidden = this.panels.length < 2;
        this.previous.addEventListener('click', () => this.activate((this.active - 1 + this.panels.length) % this.panels.length), { signal });
        this.next.addEventListener('click', () => this.activate((this.active + 1) % this.panels.length), { signal });
        this.addEventListener('shopify:block:select', event => {
          const panel = event.target.closest('[data-panel]');
          const index = this.panels.indexOf(panel);
          if (index >= 0) {
            this.setPaused('editor', true);
            this.activate(index);
          }
        }, { signal });
        this.addEventListener('shopify:block:deselect', () => this.setPaused('editor', false), { signal });
        this.addEventListener('pointerenter', event => {
          if (event.pointerType !== 'touch') this.setPaused('pointer', true);
        }, { signal });
        this.addEventListener('pointerleave', event => {
          if (event.pointerType !== 'touch') this.setPaused('pointer', false);
        }, { signal });
        this.addEventListener('focusin', event => {
          if (event.target.matches(':focus-visible')) this.setPaused('focus', true);
        }, { signal });
        this.addEventListener('focusout', event => {
          if (!this.contains(event.relatedTarget)) this.setPaused('focus', false);
        }, { signal });
        document.addEventListener('visibilitychange', () => this.setPaused('document', document.hidden), { signal });
        this.visibilityObserver = new IntersectionObserver(entries => {
          this.setPaused('viewport', !entries[0].isIntersecting);
        }, { threshold: 0.1 });
        this.visibilityObserver.observe(this);
      } else {
        this.previous.addEventListener('click', () => this.scrollRail(-1), { signal });
        this.next.addEventListener('click', () => this.scrollRail(1), { signal });
        this.items.addEventListener('scroll', () => this.updateRail(), { signal, passive: true });
        this.observer = new ResizeObserver(() => this.updateRail());
        this.observer.observe(this.items);
        this.updateRail();
      }
    }
    activate(index) {
      this.active = index;
      this.panels.forEach((panel, position) => { panel.hidden = position !== index; });
      this.tabs.forEach((tab, position) => {
        tab.dataset.active = String(position === index);
        if (position === index) tab.setAttribute('aria-current', 'true');
        else tab.removeAttribute('aria-current');
      });
      this.scheduleAutoplay();
    }
    setPaused(reason, paused) {
      if (paused) this.pauseReasons.add(reason);
      else this.pauseReasons.delete(reason);
      this.scheduleAutoplay();
    }
    scheduleAutoplay() {
      clearTimeout(this.autoplayTimer);
      this.autoplayTimer = null;
      if (!this.isConnected || !this.autoplayEnabled || this.panels.length < 2 || this.pauseReasons.size) return;
      this.autoplayTimer = setTimeout(() => {
        this.autoplayTimer = null;
        this.activate((this.active + 1) % this.panels.length);
      }, this.autoplayDelay);
    }
    scrollRail(direction) {
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.items.scrollBy({ left: direction * this.items.clientWidth * 0.8, behavior: reduced ? 'instant' : 'smooth' });
    }
    updateRail() {
      const max = this.items.scrollWidth - this.items.clientWidth;
      this.controls.hidden = this.dataset.showRailArrows !== 'true' || max <= 2;
      const style = getComputedStyle(this.items);
      const startPadding = parseFloat(style.paddingLeft) || 0;
      const endPadding = parseFloat(style.paddingRight) || 0;
      this.previous.disabled = this.items.scrollLeft <= startPadding + 2;
      this.next.disabled = this.items.scrollLeft >= max - endPadding - 2;
    }
    disconnectedCallback() {
      clearTimeout(this.autoplayTimer);
      this.autoplayTimer = null;
      this.controller?.abort();
      this.controller = null;
      this.observer?.disconnect();
      this.visibilityObserver?.disconnect();
    }
  });
}
